import type { ILineItem } from "../Summary";
import Base from "./Base";

export default class Valve extends Base {
	public static optionSelector = 'input[name="valves"]';
	private readonly customElements: {
		container: HTMLDivElement;
		placementContainer: HTMLDivElement;
		placementInput: HTMLInputElement;
		sideInput: HTMLSelectElement | undefined;
	};

	/** @param onChange */
	constructor(onChange: () => void) {
		super(
			"valved",
			() => {
				onChange();
			},
			Valve.optionSelector,
			"exto-select-valves",
		);

		this.customElements = {
			container: document.querySelector(
				".exto-custom-valve-container",
			) as HTMLDivElement,
			placementContainer: document.querySelector(
				".exto-custom-valves-placement-container",
			) as HTMLDivElement,
			placementInput: document.querySelector(
				".exto-custom-valves-placement",
			) as HTMLInputElement,
			sideInput: document.querySelector(".exto-custom-valves-side") as
				| HTMLSelectElement
				| undefined,
		};

		this.addEventListeners();
	}

	public errorMessage(): string {
		if (!this.isValid()) {
			return "Please fill out custom valve placement information";
		}
		return "";
	}

	public getLineItems(): Array<ILineItem> {
		const output = [];
		if (this.isAdded) {
			const lineItem = this.createValveLineItem();
			output.push(lineItem);
			if (this.isCustomSelected()) {
				if (this.isProductMultiSided()) {
					const sideLineItem = this.createSideLineItem();
					output.push(sideLineItem);
				}
				const placementLineItem = this.createPlacementLineItem();
				output.push(placementLineItem);
			}
		}
		return output;
	}

	public isValid() {
		if (!this.isAdditionalConfigurationRequired()) {
			return true;
		}

		if (this.isProductMultiSided()) {
			return this.isSideIndicated() && this.isPlacementIndicated();
		}
		return this.isPlacementIndicated();
	}

	protected createDetailsForPHP() {
		const base = super.createDetailsForPHP();
		const { placementInput, sideInput } = this.customElements;
		if (this.isCustomSelected()) {
			if (this.isProductMultiSided() && sideInput) {
				base.customSide = sideInput.value.substring(0, 100);
			}
			base.customPlacement = placementInput.value.substring(0, 100);
		}
		return base;
	}

	/**
	 * @param event
	 * @param newActiveValve
	 */
	protected setActiveOption(event: Event, newActiveValve: HTMLElement) {
		super.setActiveOption(event, newActiveValve);
		this.updateCustomDisplay();
	}

	protected updateCustomDisplay() {
		if (this.isCustomSelected()) {
			this.displayCustomValveOptions();
		} else {
			this.hideCustomValveOptions();
		}
	}

	private addEventListeners() {
		const { placementInput, sideInput } = this.customElements;
		placementInput.addEventListener("input", () => {
			this.handlePlacementInputChange();
		});
		if (this.isProductMultiSided() && sideInput) {
			sideInput.addEventListener("input", () => {
				this.handleSideInputChange();
			});
		}
	}

	private createPlacementLineItem() {
		const placementInfo = this.customElements.placementInput.value.substring(
			0,
			100,
		);
		const positionLineItem: ILineItem = {
			description: placementInfo,
			key: "",
			label: "Valve Placement Position",
			value: 0,
		};
		return positionLineItem;
	}

	private createSideLineItem() {
		const { sideInput } = this.customElements;
		if (!sideInput) {
			throw new Error("Side Input Not Set");
		}
		const sideInfo = sideInput.value.substring(0, 100);
		const sideLineItem: ILineItem = {
			description: sideInfo,
			key: "",
			label: "Valve Placement Side",
			value: 0,
		};
		return sideLineItem;
	}

	private createValveLineItem() {
		const lineItem: ILineItem = {
			description: this.getActiveLabel(),
			key: "",
			label: "Valves",
			value: this.getActiveOptionCost(),
		};
		return lineItem;
	}

	private displayCustomValveOptions() {
		this.customElements.container.classList.remove(this.hiddenClass);
		if (this.isProductMultiSided() && !this.customElements.sideInput?.value) {
			this.hidePlacementContainer();
		}
	}

	private handlePlacementInputChange() {
		this.onChange();
	}

	private handleSideInputChange() {
		const { sideInput } = this.customElements;

		if (sideInput?.value !== "") {
			this.showPlacementContainer();
		} else {
			this.hidePlacementContainer();
		}
		this.onChange();
	}

	private hideCustomValveOptions() {
		this.customElements.container.classList.add(this.hiddenClass);
	}

	private hidePlacementContainer() {
		this.customElements.placementContainer.classList.add(this.hiddenClass);
	}

	private isAdditionalConfigurationRequired() {
		return this.isAdded && this.isCustomSelected();
	}

	private isCustomSelected() {
		return this.active.classList.contains("exto-custom-valves");
	}

	private isPlacementIndicated() {
		return this.customElements.placementInput.value !== "";
	}

	private isProductMultiSided() {
		return this.customElements.sideInput !== null;
	}

	private isSideIndicated() {
		return this.customElements.sideInput?.value !== "";
	}

	private showPlacementContainer() {
		this.customElements.placementContainer.classList.remove(this.hiddenClass);
	}
}
